import { EventTracker, EventType } from './eventTracker';
import { SessionManager } from './session';

/**
 * Información básica del usuario
 */
export interface UserInfo {
  userId: string;
  name?: string;
  email?: string;
  role?: string;
  [key: string]: any;
}

/**
 * Clase para trackear eventos relacionados con usuarios
 */
export class UserTracker {
  public apiUrl: string;
  private eventTracker: EventTracker;
  private sessionManager: SessionManager;
  
  constructor(apiUrl: string, sessionManager: SessionManager, eventTracker: EventTracker) {
    this.apiUrl = apiUrl;
    this.sessionManager = sessionManager;
    this.eventTracker = eventTracker;
  }
  
  /**
   * Trackea un evento de registro de usuario
   * @param user Información del usuario registrado
   */
  trackRegister(user: UserInfo): void {
    // Actualizar el ID de usuario en el gestor de sesiones
    this.sessionManager.setUserId(user.userId);
    
    // Rastrear el evento de registro
    this.eventTracker.trackImmediate(
      EventType.USER_REGISTER,
      {
        user: {
          userId: user.userId,
          name: user.name,
          email: user.email,
          role: user.role
        }
      }
    );
  }
  
  /**
   * Trackea un evento de inicio de sesión
   * @param user Información del usuario que inicia sesión
   */
  async trackLogin(user: UserInfo): Promise<void> {
    // Actualizar el ID de usuario en el gestor de sesiones
    this.sessionManager.setUserId(user.userId);
    
    // Vincular la sesión anónima al usuario
    try {
      await this.sessionManager.linkSessionToUser(user.userId);
    } catch (error) {
      console.error('Error linking session to user:', error);
    }
    
    // Rastrear el evento de inicio de sesión
    this.eventTracker.trackImmediate(
      EventType.USER_LOGIN,
      {
        user: {
          userId: user.userId,
          name: user.name,
          email: user.email,
          role: user.role
        }
      }
    );
  }
  
  /**
   * Trackea un evento de cierre de sesión
   */
  trackLogout(): void {
    // Obtener ID de usuario antes de limpiarlo
    const userId = this.sessionManager.getUserId();
    
    // Rastrear el evento de cierre de sesión
    if (userId) {
      this.eventTracker.trackImmediate(
        EventType.USER_LOGOUT,
        {
          userId
        }
      );
    }
    
    // Limpiar el ID de usuario en el gestor de sesiones
    this.sessionManager.clearUserId();
  }
  
  /**
   * Trackea un evento de actualización de perfil
   * @param user Información del usuario actualizada
   * @param changedFields Campos que fueron actualizados
   */
  trackProfileUpdate(user: UserInfo, changedFields: string[]): void {
    this.eventTracker.track(
      EventType.CUSTOM,
      {
        eventName: 'profile_update',
        user: {
          userId: user.userId,
          name: user.name,
          email: user.email,
          role: user.role
        },
        changedFields
      }
    );
  }
  
  /**
   * Identifica al usuario actual para el tracking
   * Debe llamarse cuando la aplicación carga y el usuario ya está autenticado
   * @param user Información del usuario
   */
  identifyUser(user: UserInfo): void {
    if (!user.userId) {
      console.error('User ID is required for identification');
      return;
    }
    
    // Actualizar el ID de usuario en el gestor de sesiones
    this.sessionManager.setUserId(user.userId);
  }
  
  /**
   * Verifica si hay un usuario identificado actualmente
   */
  hasIdentifiedUser(): boolean {
    return this.sessionManager.isAuthenticated();
  }
  
  /**
   * Obtiene el ID del usuario actual si está autenticado
   */
  getCurrentUserId(): string | null {
    return this.sessionManager.getUserId();
  }
} 